import { LOCALISATION_CONFIG } from "@/shared/config/localisation";

type NationalIdConfig = {
  label: string;
  frontLabel: string;
  backLabel: string;
  placeholder: string;
  maxLength: number;
  validationMessage: string;
  format: (value: unknown) => string;
  isValid: (value: string) => boolean;
  getBirthYear: (value: string) => string | null;
  ocrKeys: string[];
};

const formatEmiratesIdValue = (value: unknown) => {
  const digits = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 15);

  return [digits.slice(0, 3), digits.slice(3, 7), digits.slice(7, 14), digits.slice(14, 15)]
    .filter(Boolean)
    .join("-");
};

const formatAadhaarValue = (value: unknown) => {
  const digits = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 12);

  return [digits.slice(0, 4), digits.slice(4, 8), digits.slice(8, 12)].filter(Boolean).join(" ");
};

const NATIONAL_ID_CONFIGS: Record<string, NationalIdConfig> = {
  IND: {
    label: "Aadhaar Card",
    frontLabel: "Aadhaar Card (Front)",
    backLabel: "Aadhaar Card (Back)",
    placeholder: "1234 5678 9012",
    maxLength: 14,
    validationMessage: "Aadhaar Card must be in the format XXXX XXXX XXXX",
    format: formatAadhaarValue,
    isValid: (value) => /^\d{4}\s\d{4}\s\d{4}$/.test(value),
    getBirthYear: () => null,
    ocrKeys: ["Aadhaar_Number", "Aadhaar Number", "Aadhaar No", "AADHAAR", "Aadhaar"],
  },
  DEFAULT: {
    label: "Emirates ID",
    frontLabel: "Emirates ID (Front)",
    backLabel: "Emirates ID (Back)",
    placeholder: "784-XXXX-XXXXXXX-X",
    maxLength: 18,
    validationMessage: "Emirates ID must be in the format 784-XXXX-XXXXXXX-X",
    format: formatEmiratesIdValue,
    isValid: (value) => /^784-\d{4}-\d{7}-\d$/.test(value),
    getBirthYear: (value) => {
      const match = /^784-(\d{4})-\d{7}-\d$/.exec(value);
      return match?.[1] || null;
    },
    ocrKeys: ["Emirates_Id", "Emirates ID", "Id Number", "Id_Number"],
  },
};

const getCountryKey = () =>
  String(LOCALISATION_CONFIG.country || "")
    .trim()
    .toUpperCase();

export const getNationalIdConfig = (): NationalIdConfig => {
  const countryKey = getCountryKey();
  return NATIONAL_ID_CONFIGS[countryKey] || NATIONAL_ID_CONFIGS.DEFAULT;
};

export const getNationalIdLabel = () => getNationalIdConfig().label;

export const getNationalIdFrontLabel = () => getNationalIdConfig().frontLabel;

export const getNationalIdBackLabel = () => getNationalIdConfig().backLabel;

export const getNationalIdPlaceholder = () => getNationalIdConfig().placeholder;

export const getNationalIdMaxLength = () => getNationalIdConfig().maxLength;

export const getNationalIdValidationMessage = () => getNationalIdConfig().validationMessage;

export const formatNationalId = (value: unknown) => getNationalIdConfig().format(value);

export const isValidNationalId = (value: string) => getNationalIdConfig().isValid(value);

export const getNationalIdBirthYear = (value: string) => getNationalIdConfig().getBirthYear(value);

export const getNationalIdOcrKeys = () => getNationalIdConfig().ocrKeys;
